import { useEffect, useState } from 'react';

import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { EmailIcon } from '@assets/icons';
import { AlertType } from '@enums/alert';
import { ErrorMessages } from '@enums/error';
import { AuthLayout } from '@layout/index';
import { showAlert } from '@lib/alert/alert-manager';
import { useLogin } from '@pages/login/hooks/use-login';
import { AppRoutes } from '@routes/constants';
import { LocalStorageKeys, SessionStorageKeys } from '@utils/auth';

import BrandtechTermsModal from './components/BrandtechTerms';
import LoginForm from './components/LoginForm';
import UnileverTermsModal from './components/UnileverTerms';
import { LoginInputFlow } from './enums';

// Types
interface LoginCredentials {
  email: string;
  password: string;
}

interface StyledLinkProps {
  onClick: () => void;
  children: React.ReactNode;
}

// Components
const StyledLink = ({ onClick, children }: StyledLinkProps) => (
  <button onClick={onClick} className="text-blue-5 font-medium">
    {children}
  </button>
);

const FormContainer = ({
  isVisible,
  children,
}: {
  isVisible: boolean;
  children: React.ReactNode;
}) => (
  <div
    className={twMerge(
      classNames(
        'w-full overflow-hidden transition-all duration-500 ease-in-out transform',
        isVisible
          ? 'max-h-[290px] opacity-100 translate-y-0'
          : 'max-h-0 opacity-0 -translate-y-4',
      ),
    )}
  >
    {children}
  </div>
);

const ToggleText = ({
  activeFlow,
  onToggle,
}: {
  activeFlow: LoginInputFlow | null;
  onToggle: (flow: LoginInputFlow) => void;
}) => (
  <div className="text-center text-sm text-black transition-all duration-300">
    {activeFlow === LoginInputFlow.EMAIL ? (
      <>
        Already have a Unilever account?{' '}
        <StyledLink onClick={() => onToggle(LoginInputFlow.SSO)}>
          Login with Unilever account
        </StyledLink>
      </>
    ) : (
      <>
        Don't have a Unilever account?{' '}
        <StyledLink onClick={() => onToggle(LoginInputFlow.EMAIL)}>
          Sign-in as an external user
        </StyledLink>
      </>
    )}
  </div>
);

const Footer = ({
  onUnileverTermsClick,
  onBrandtechTermsClick,
}: {
  onUnileverTermsClick: () => void;
  onBrandtechTermsClick: () => void;
}) => (
  <div className="flex flex-col mt-6 text-center text-sm space-y-1">
    <div>
      By logging into your account, you agree to the Terms and
      <br />
      Conditions set below.
    </div>
    <StyledLink onClick={onUnileverTermsClick}>
      Unilever Terms & Conditions
    </StyledLink>
    <StyledLink onClick={onBrandtechTermsClick}>
      Brandtech Group Terms & Conditions
    </StyledLink>
  </div>
);

const Login = () => {
  const navigate = useNavigate();

  const { mutateAsync: emailPasswordLogin } = useLogin();

  const [activeFlow, setActiveFlow] = useState<LoginInputFlow>(
    LoginInputFlow.SSO,
  );
  const [isTermsModalOpen, setTermsModalOpen] = useState(false);
  const [isBrandtechTermsModalOpen, setBrandtechTermsModalOpen] =
    useState(false);

  // Handlers
  const handleSignInSuccess = () => {
    localStorage.setItem(LocalStorageKeys.IS_LOGGED_IN, 'true');
    navigate(AppRoutes.SELECT_WORKSPACE);
  };

  const handleLoginButtonClick = async (credentials: LoginCredentials) => {
    try {
      await emailPasswordLogin(credentials);
      handleSignInSuccess();
    } catch (error) {
      console.warn(error);
    }
  };

  const handleToggleFlow = (newFlow: LoginInputFlow) => {
    setActiveFlow(newFlow);
  };

  // Effects
  // To show session expired message
  useEffect(() => {
    if (
      sessionStorage.getItem(SessionStorageKeys.IS_TOKEN_EXPIRED) === 'true'
    ) {
      showAlert({
        type: AlertType.ERROR,
        message: ErrorMessages.APP_SESSION_EXPIRED,
      });
      sessionStorage.removeItem(SessionStorageKeys.IS_TOKEN_EXPIRED);
    }
  }, []);

  return (
    <AuthLayout>
      <div className="flex flex-col max-w-[476px] w-full gap-4">
        <FormContainer isVisible={activeFlow !== null}>
          {activeFlow === LoginInputFlow.SSO ? (
            <LoginForm
              buttonLabel="Login with your Unilever account"
              isSSOLogin
            />
          ) : (
            <LoginForm
              buttonLabel="Sign-in for external users"
              iconComponent={EmailIcon}
              onLoginButtonClick={handleLoginButtonClick}
            />
          )}
        </FormContainer>
        <ToggleText activeFlow={activeFlow} onToggle={handleToggleFlow} />
        <Footer
          onUnileverTermsClick={() => setTermsModalOpen(true)}
          onBrandtechTermsClick={() => setBrandtechTermsModalOpen(true)}
        />
      </div>
      <BrandtechTermsModal
        isOpen={isBrandtechTermsModalOpen}
        onClose={() => setBrandtechTermsModalOpen(false)}
      />
      <UnileverTermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setTermsModalOpen(false)}
      />
    </AuthLayout>
  );
};

export default Login;
